import { useState, useRef } from "react";
import {
  Car,
  Calendar,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Upload,
  X,
  Check,
  Clock,
  Zap,
  AlertTriangle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { StepProgress, AnimatedButton } from "./motion";
import { services, carBrands } from "../data/serviceData";
import { formatRand } from "../lib/invoice";
import { useTheme } from "../context/ThemeContext";

const TIMES = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
  "4:00 PM", "5:00 PM", "6:00 PM",
];

function getDates() {
  const dates = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    dates.push(d);
  }
  return dates;
}

const DATES = getDates();

export default function BookingInterface({ selectedServices, urgentMode }) {
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState({ brand: "", model: "", year: "", plate: "" });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [location, setLocation] = useState("");
  const [uploads, setUploads] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const STEPS = [
    { label: "Services", icon: Zap },
    { label: "Vehicle", icon: Car },
    { label: "Schedule", icon: Calendar },
    { label: "Location", icon: MapPin },
  ];

  const selectedServiceData = services.filter((s) =>
    selectedServices.includes(s.id)
  );
  const subtotal = selectedServiceData.reduce((acc, s) => acc + s.priceMin, 0);
  const urgentFee = urgentMode ? 650 : 0;
  const total = subtotal + urgentFee;
  const totalDuration = selectedServiceData.reduce((acc, s) => acc + s.duration, 0);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploads((prev) => [
          ...prev,
          { name: file.name, type: file.type, url: ev.target.result },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const canProceed = () => {
    if (step === 0) return selectedServices.length > 0;
    if (step === 1) return vehicle.brand && vehicle.model && vehicle.year;
    if (step === 2) return selectedDate && selectedTime;
    if (step === 3) return location.trim().length > 4;
    return false;
  };

  if (submitted) {
    return (
      <section id="booking" className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(0,240,255,0.3)]">
            <Check size={36} className="text-[#00F0FF]" />
          </div>
          <h2 className={`font-display font-bold text-4xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
            Booking Confirmed!
          </h2>
          <p className="text-[#8F9098] mb-8">
            Your mechanic is on the way. Track them in real time below.
          </p>
          <button
            onClick={() => {
              document.querySelector("#tracking")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#00F0FF] text-black font-bold"
          >
            Track Your Mechanic
            <ChevronRight size={16} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className={`text-xs uppercase tracking-[0.2em] mb-3 font-mono font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
            Book a mechanic
          </p>
          <h2 className={`font-display font-bold text-4xl lg:text-5xl tracking-tighter ${isDark ? "text-white" : "text-slate-900"}`}>
            Booking Interface
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Steps form */}
          <div className="lg:col-span-2">
            {/* Step indicator */}
            <StepProgress steps={STEPS} currentStep={step} />

            <AnimatePresence mode="wait">
            {/* Step 0: Services */}
            {step === 0 && (
              <motion.div
                key="step-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h3 className={`font-display text-xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
                  Selected Services
                </h3>
                {selectedServices.length === 0 ? (
                  <p className={`text-sm p-6 rounded-xl border border-dashed text-center ${isDark ? "text-[#8F9098] border-[#222226]" : "text-slate-500 border-slate-300"}`}>
                    No services selected.{" "}
                    <button
                      onClick={() =>
                        document
                          .querySelector("#services")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                      className={isDark ? "text-[#00F0FF] underline" : "text-brand-blue underline"}
                    >
                      Browse services above
                    </button>
                  </p>
                ) : (
                  <div className="space-y-3">
                    {selectedServiceData.map((svc) => {
                      const Icon = svc.Icon;
                      return (
                        <div
                          key={svc.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border ${isDark ? "bg-[#141419] border-[#222226]" : "bg-white border-slate-200 shadow-[var(--shadow-card)]"}`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? "bg-[#00F0FF]/10" : "bg-brand-teal/10"}`}>
                            <Icon size={16} className={isDark ? "text-[#00F0FF]" : "text-brand-teal"} />
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{svc.name}</p>
                            <p className={`text-xs ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>{svc.duration} min</p>
                          </div>
                          <span className={`font-mono text-xs font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-blue"}`}>
                            from {formatRand(svc.priceMin)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 1: Vehicle */}
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h3 className={`font-display text-xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
                  Vehicle Details
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                        Make
                      </label>
                      <select
                        data-testid="vehicle-brand-select"
                        value={vehicle.brand}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, brand: e.target.value, model: "" })
                        }
                        className={`w-full rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none ${isDark ? "bg-[#0C0C0F] border border-[#222226] text-white" : "bg-white border border-slate-200 text-slate-900"}`}
                      >
                        <option value="">Select make</option>
                        {carBrands.map((b) => (
                          <option key={b.name} value={b.name}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                        Model
                      </label>
                      <select
                        data-testid="vehicle-model-select"
                        value={vehicle.model}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, model: e.target.value })
                        }
                        disabled={!vehicle.brand}
                        className={`w-full rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none disabled:opacity-40 ${isDark ? "bg-[#0C0C0F] border border-[#222226] text-white" : "bg-white border border-slate-200 text-slate-900"}`}
                      >
                        <option value="">Select model</option>
                        {(
                          carBrands.find((b) => b.name === vehicle.brand)?.models || []
                        ).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                        Year
                      </label>
                      <select
                        data-testid="vehicle-year-select"
                        value={vehicle.year}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, year: e.target.value })
                        }
                        className={`w-full rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none ${isDark ? "bg-[#0C0C0F] border border-[#222226] text-white" : "bg-white border border-slate-200 text-slate-900"}`}
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 26 }, (_, i) => 2026 - i).map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                        Number Plate (optional)
                      </label>
                      <input
                        data-testid="vehicle-plate-input"
                        type="text"
                        placeholder="CA 123-456"
                        value={vehicle.plate}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, plate: e.target.value.toUpperCase() })
                        }
                        className={`w-full rounded-md px-3 py-2.5 text-sm placeholder-[#8F9098]/50 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none font-mono ${isDark ? "bg-[#0C0C0F] border border-[#222226] text-white" : "bg-white border border-slate-200 text-slate-900"}`}
                      />
                    </div>
                  </div>

                  {/* Upload */}
                  <div>
                    <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                      Photos / Video (optional)
                    </label>
                    <div
                      onClick={() => fileRef.current?.click()}
                      className="border border-dashed border-[#222226] rounded-xl p-6 text-center cursor-pointer hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/5 transition-all group"
                    >
                      <Upload
                        size={20}
                        className="mx-auto mb-2 text-[#8F9098] group-hover:text-[#00F0FF]"
                      />
                      <p className="text-sm text-[#8F9098] group-hover:text-white transition-colors">
                        Drag & drop or{" "}
                        <span className="text-[#00F0FF]">browse files</span>
                      </p>
                      <p className="text-xs text-[#8F9098]/60 mt-1">
                        JPG, PNG, MP4 up to 20MB
                      </p>
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    {uploads.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {uploads.map((u, i) => (
                          <div
                            key={i}
                            className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#222226] group"
                          >
                            {u.type.startsWith("image") ? (
                              <img
                                src={u.url}
                                alt={u.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-[#141419] flex items-center justify-center">
                                <span className="text-[10px] text-[#8F9098]">VID</span>
                              </div>
                            )}
                            <button
                              onClick={() =>
                                setUploads((prev) => prev.filter((_, j) => j !== i))
                              }
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                            >
                              <X size={14} className="text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Date & Time */}
            {step === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h3 className={`font-display text-xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
                  Pick Date & Time
                </h3>
                {/* Date selector */}
                <div className="mb-6">
                  <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-3 block">
                    Date
                  </label>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {DATES.map((d, i) => {
                      const isSelected =
                        selectedDate?.toDateString() === d.toDateString();
                      const isToday = i === 0;
                      return (
                        <button
                          key={i}
                          data-testid={`date-${i}`}
                          onClick={() => setSelectedDate(d)}
                          className={`flex-shrink-0 flex flex-col items-center px-3 py-3 rounded-xl border transition-all min-w-[60px] ${
                            isSelected
                              ? isDark
                                ? "bg-[#00F0FF]/10 border-[#00F0FF]/50 text-[#00F0FF] shadow-[0_0_16px_rgba(0,240,255,0.15)]"
                                : "bg-brand-blue/5 border-brand-blue/50 text-brand-blue shadow-sm"
                              : isDark
                                ? "bg-[#141419] border-[#222226] text-[#8F9098] hover:border-white/20 hover:text-white"
                                : "bg-white border-slate-200 text-slate-500 hover:border-brand-blue/30 hover:text-slate-900 shadow-sm"
                          }`}
                        >
                          <span className="text-[10px] uppercase tracking-wider mb-1">
                            {isToday
                              ? "Today"
                              : d.toLocaleDateString("en", { weekday: "short" })}
                          </span>
                          <span className="text-lg font-bold font-display">
                            {d.getDate()}
                          </span>
                          <span className="text-[10px]">
                            {d.toLocaleDateString("en", { month: "short" })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time selector */}
                <div>
                  <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-3 block">
                    Time
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {TIMES.map((t) => (
                      <button
                        key={t}
                        data-testid={`time-${t}`}
                        onClick={() => setSelectedTime(t)}
                        className={`py-2.5 px-3 rounded-lg border text-xs font-mono transition-all ${
                          selectedTime === t
                            ? isDark
                              ? "bg-[#00F0FF]/10 border-[#00F0FF]/50 text-[#00F0FF]"
                              : "bg-brand-blue/5 border-brand-blue/50 text-brand-blue"
                            : isDark
                              ? "bg-[#141419] border-[#222226] text-[#8F9098] hover:border-white/20 hover:text-white"
                              : "bg-white border-slate-200 text-slate-500 hover:border-brand-blue/30 hover:text-slate-900 shadow-sm"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Location */}
            {step === 3 && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h3 className={`font-display text-xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
                  Service Location
                </h3>
                <div className="mb-4">
                  <label className="text-xs text-[#8F9098] uppercase tracking-wider mb-2 block">
                    Your Address
                  </label>
                  <div className="relative">
                    <MapPin
                      size={16}
                      className="absolute left-3 top-3 text-[#8F9098]"
                    />
                    <input
                      data-testid="location-input"
                      type="text"
                      placeholder="42 Dorp Street, Stellenbosch, Western Cape"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className={`w-full rounded-md pl-9 pr-4 py-3 text-sm placeholder-[#8F9098]/50 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none ${isDark ? "bg-[#0C0C0F] border border-[#222226] text-white" : "bg-white border border-slate-200 text-slate-900"}`}
                    />
                  </div>
                </div>

                {/* Map placeholder */}
                <div className="rounded-xl border border-[#222226] overflow-hidden h-48 relative bg-[#0C0C0F]">
                  <div className="absolute inset-0 grid-pattern opacity-30" />
                  {/* Fake map pins */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200">
                    {/* Grid lines */}
                    {[40, 80, 120, 160, 200].map((y) => (
                      <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#ffffff" strokeOpacity="0.04" strokeWidth="1" />
                    ))}
                    {[80, 160, 240, 320].map((x) => (
                      <line key={x} x1={x} y1="0" x2={x} y2="200" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="1" />
                    ))}
                    {/* Route */}
                    <path
                      d="M 60 160 Q 120 100 200 100 Q 280 100 320 60"
                      fill="none"
                      stroke="#00F0FF"
                      strokeWidth="2"
                      strokeOpacity="0.4"
                      className="route-dash"
                    />
                    {/* Mechanic dot */}
                    <circle cx="60" cy="160" r="6" fill="#22E584" opacity="0.9" />
                    <circle cx="60" cy="160" r="12" fill="#22E584" opacity="0.2" />
                    {/* Destination */}
                    <circle cx="320" cy="60" r="8" fill="#00F0FF" opacity="0.8" />
                    <circle cx="320" cy="60" r="16" fill="#00F0FF" opacity="0.15" />
                  </svg>
                  <div className="absolute bottom-3 left-3 glass rounded-lg px-3 py-1.5">
                    <p className="text-xs text-[#00F0FF] font-mono">Coverage map — 40 km radius</p>
                  </div>
                </div>

                {urgentMode && (
                  <div className="mt-4 flex items-center gap-3 p-4 rounded-xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30">
                    <AlertTriangle size={16} className="text-[#FF2A5F] flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-white">Emergency mode active</p>
                      <p className="text-xs text-[#8F9098]">
                        Priority dispatch — estimated arrival 25–40 min. +R650 urgency fee applies.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="flex justify-between mt-8">
              <AnimatedButton
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed ${isDark ? "border-[#222226] text-[#8F9098] hover:border-white/30 hover:text-white" : "border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-900"}`}
              >
                <ChevronLeft size={14} />
                Back
              </AnimatedButton>

              {step < STEPS.length - 1 ? (
                <AnimatedButton
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "bg-[#00F0FF] text-black shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:bg-white" : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_4px_16px_rgba(249,115,22,0.3)] hover:shadow-[0_6px_24px_rgba(249,115,22,0.4)]"}`}
                >
                  Continue
                  <ChevronRight size={14} />
                </AnimatedButton>
              ) : (
                <AnimatedButton
                  data-testid="confirm-booking-button"
                  onClick={() => setSubmitted(true)}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "bg-[#00F0FF] text-black shadow-[0_0_24px_rgba(0,240,255,0.4)] hover:bg-white" : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_4px_16px_rgba(249,115,22,0.3)] hover:shadow-[0_6px_24px_rgba(249,115,22,0.4)]"}`}
                >
                  <Check size={14} />
                  Confirm Booking
                </AnimatedButton>
              )}
            </div>
          </div>

          {/* Right: Summary panel */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div
                className={`rounded-2xl border p-6 transition-all ${isDark ? "" : "bg-white shadow-[var(--shadow-card)]"}`}
                style={isDark ? {
                  background: "linear-gradient(135deg, rgba(20,20,25,0.95), rgba(12,12,15,0.95))",
                  borderColor: selectedServices.length > 0 ? "rgba(0,240,255,0.3)" : "#222226",
                  boxShadow:
                    selectedServices.length > 0
                      ? "0 0 40px -8px rgba(0,240,255,0.2), inset 0 0 0 1px rgba(0,240,255,0.05)"
                      : "none",
                } : {
                  borderColor: selectedServices.length > 0 ? "rgba(30,64,175,0.25)" : "#e2e8f0",
                }}
              >
                <h3 className={`font-display font-semibold text-sm uppercase tracking-wider mb-5 flex items-center gap-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                  <div className={`w-1.5 h-4 rounded-sm ${isDark ? "bg-[#00F0FF]" : "bg-brand-blue"}`} />
                  Booking Summary
                </h3>

                {selectedServiceData.length === 0 ? (
                  <p className="text-[#8F9098] text-xs text-center py-4">
                    No services selected yet
                  </p>
                ) : (
                  <>
                    <div className="space-y-3 mb-5">
                      {selectedServiceData.map((svc) => {
                        const Icon = svc.Icon;
                        return (
                          <div key={svc.id} className="flex items-center gap-3">
                            <Icon size={14} className="text-[#8F9098] flex-shrink-0" />
                            <span className="text-white/80 text-xs flex-1">{svc.name}</span>
                            <span className="font-mono text-xs text-[#8F9098]">
                              {formatRand(svc.priceMin)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="border-t border-[#222226] pt-4 space-y-2">
                      {urgentMode && (
                        <div className="flex justify-between text-xs">
                          <span className="text-[#FF2A5F]">Emergency fee</span>
                          <span className="font-mono text-[#FF2A5F]">+{formatRand(650)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-white font-semibold">Estimated total</span>
                        <span className="font-mono text-sm text-[#00F0FF] font-bold">
                          from {formatRand(total)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-[#8F9098] mt-1">
                        <Clock size={11} />
                        <span>~{totalDuration} min job time</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Schedule summary */}
                {(selectedDate || selectedTime || location) && (
                  <div className="mt-5 pt-5 border-t border-[#222226] space-y-2">
                    {vehicle.brand && (
                      <div className="flex items-center gap-2 text-xs text-[#8F9098]">
                        <Car size={11} className="text-[#00F0FF]" />
                        {vehicle.year} {vehicle.brand} {vehicle.model}
                      </div>
                    )}
                    {selectedDate && selectedTime && (
                      <div className="flex items-center gap-2 text-xs text-[#8F9098]">
                        <Calendar size={11} className="text-[#00F0FF]" />
                        {selectedDate.toLocaleDateString("en", {
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        @ {selectedTime}
                      </div>
                    )}
                    {location && (
                      <div className="flex items-center gap-2 text-xs text-[#8F9098]">
                        <MapPin size={11} className="text-[#00F0FF]" />
                        <span className="truncate">{location}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
